import type {
  HistoryServiceV2,
  HistoryServiceV3,
} from '@feature-hub/history-service';
import React, {
  type PropsWithChildren,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react';

type PageViewTrackingContext = {
  referrer: string;
};

export const PageViewTrackContext =
  createContext<PageViewTrackingContext | null>(null);

// mandatory or not should be statically configured
export function usePageViewTrackContext(
  mandatory?: true,
): PageViewTrackingContext;
export function usePageViewTrackContext(
  mandatory: false,
): PageViewTrackingContext | null;
export function usePageViewTrackContext(mandatory = true) {
  const env = React.useContext(PageViewTrackContext);

  if (!mandatory) {
    return env;
  }

  if (!env) {
    throw new Error(`PageViewTrackContextProvider is missing.`);
  }

  return env;
}

function getReferrerUrl(
  historyService: HistoryServiceV2 | HistoryServiceV3,
): string {
  const {pathname, search} = historyService.rootHistory.location;
  return new URL(
    historyService.rootHistory.createHref({pathname, search}),
    document.location.origin,
  ).href;
}

interface PageViewTrackContextProviderProps {
  historyService: HistoryServiceV2 | HistoryServiceV3;
}
export const PageViewTrackContextProvider = ({
  children,
  historyService,
}: PropsWithChildren<PageViewTrackContextProviderProps>) => {
  // Avoided using document.referrer, since the behavior is
  // a bit unstable, and it's better to leave empty than incorrect
  // for feature-apps
  const [currentReferrer, setCurrentReferrer] = useState(``);
  const nextReferrerRef = useRef(``);

  useEffect(() => {
    nextReferrerRef.current = getReferrerUrl(historyService);

    return historyService.rootHistory.listen(() => {
      const nextReferrer = getReferrerUrl(historyService);

      if (nextReferrer !== nextReferrerRef.current) {
        setCurrentReferrer(nextReferrerRef.current);
        nextReferrerRef.current = nextReferrer;
      }
    });
  }, [historyService]);

  const value = {
    referrer: currentReferrer,
  };
  return (
    <PageViewTrackContext.Provider value={value}>
      {children}
    </PageViewTrackContext.Provider>
  );
};
